import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  Calendar, Plus, Trash2, Check, Clock, Download,
  ChevronLeft, ChevronRight, Bell, Repeat, Link2, Sun, Moon, Sunrise, CalendarPlus, Star, CircleDot,
} from 'lucide-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  getPlannerEvents,
  createPlannerEvent,
  updatePlannerEvent,
  deletePlannerEvent,
  getCustomActivities,
  getPrayerTimes,
  getUserPrayerSettings,
  getPrayersToday,
  updatePrayer,
  getTodayBarakahPlan,
  saveTodayBarakahPlan,
  createPlanItemCalendarBlock,
  getPlannedVsActualTimeline,
  formatPlannerDateTimeLocalInput,
  createDefaultTodayPlan,
  createEmptyTodayPlanPriority,
  hasPriorityTimeRange,
  hasTodayPlanContent,
} from '../lib/api';
import { getPriorityWarningMap, validatePriorityRanges } from '../lib/plannerPriorityValidation';
import { usePlannerPriorityTimeNormalization } from '../hooks/usePlannerPriorityTimeNormalization';
import PlannedVsActualView from '../components/PlannedVsActualView';
import { SortableTodayPlanGoalCard } from '../components/planner/SortableTodayPlanGoalCard';

// Islamic Calendar Events for 2026 (Hijri 1447-1448)
const ISLAMIC_EVENTS_2026 = [
  { date: '2025-12-20', name_en: 'Rajab Begins', name_hr: 'Početak Redžeba', type: 'month' },
  { date: '2026-01-16', name_en: "Isra and Mi'raj", name_hr: "Isra' i Mi'radž", type: 'holy' },
  { date: '2026-01-19', name_en: "Sha'ban Begins", name_hr: "Početak Ša'bana", type: 'month' },
  { date: '2026-02-04', name_en: 'Laylat al-Bara\'at', name_hr: 'Noć polovice Ša\'bana', type: 'holy' },
  { date: '2026-02-18', name_en: 'Ramadan Begins', name_hr: 'Početak Ramazana', type: 'ramadan' },
  { date: '2026-03-15', name_en: 'Laylat al-Qadr (estimated)', name_hr: 'Lejletul-kadr (procjena)', type: 'holy' },
  { date: '2026-03-20', name_en: 'Eid al-Fitr', name_hr: 'Ramazanski bajram', type: 'eid' },
  { date: '2026-03-21', name_en: 'Eid al-Fitr (Day 2)', name_hr: 'Ramazanski bajram (2. dan)', type: 'eid' },
  { date: '2026-03-22', name_en: 'Eid al-Fitr (Day 3)', name_hr: 'Ramazanski bajram (3. dan)', type: 'eid' },
  { date: '2026-05-26', name_en: 'Day of Arafah', name_hr: 'Dan Arefata', type: 'holy' },
  { date: '2026-05-25', name_en: 'Hajj Begins', name_hr: 'Početak Hadždža', type: 'hajj' },
  { date: '2026-05-27', name_en: 'Eid al-Adha', name_hr: 'Kurban bajram', type: 'eid' },
  { date: '2026-05-28', name_en: 'Eid al-Adha (Day 2)', name_hr: 'Kurban bajram (2. dan)', type: 'eid' },
  { date: '2026-05-29', name_en: 'Eid al-Adha (Day 3)', name_hr: 'Kurban bajram (3. dan)', type: 'eid' },
  { date: '2026-06-16', name_en: 'Islamic New Year 1448', name_hr: 'Islamska nova godina 1448', type: 'newyear' },
  { date: '2026-06-26', name_en: 'Ashura', name_hr: 'Ašura', type: 'holy' },
  { date: '2026-08-25', name_en: 'Mawlid al-Nabi', name_hr: 'Mevlud', type: 'holy' },
];

// Function to get upcoming Islamic events
const getUpcomingIslamicEvents = (fromDate, count = 3) => {
  const from = new Date(`${fromDate}T00:00:00`);
  from.setHours(0, 0, 0, 0);
  
  return ISLAMIC_EVENTS_2026
    .filter(event => new Date(`${event.date}T00:00:00`) >= from)
    .slice(0, count);
};

// Function to check if date has Islamic event
const getIslamicEventForDate = (dateStr) => {
  return ISLAMIC_EVENTS_2026.find(event => event.date === dateStr);
};

const EVENT_TYPES = [
  { id: 'prayer', label_en: 'Prayer', label_hr: 'Namaz', color: 'bg-green-500', icon: '🕌' },
  { id: 'quran', label_en: 'Quran', label_hr: "Kur'an", color: 'bg-blue-500', icon: '📖' },
  { id: 'dhikr', label_en: 'Dhikr', label_hr: 'Zikr', color: 'bg-purple-500', icon: '📿' },
  { id: 'sadaqah', label_en: 'Sadaqah', label_hr: 'Sadaka', color: 'bg-pink-500', icon: '💝' },
  { id: 'learning', label_en: 'Learning', label_hr: 'Učenje', color: 'bg-yellow-500', icon: '📚' },
  { id: 'general', label_en: 'General', label_hr: 'Općenito', color: 'bg-stone-500', icon: '📌' },
];

const PRAYER_NAMES = {
  Fajr: { en: 'Fajr', hr: 'Sabah', icon: '🌅' },
  Sunrise: { en: 'Sunrise', hr: 'Izlazak sunca', icon: '☀️' },
  Dhuhr: { en: 'Dhuhr', hr: 'Podne', icon: '🌞' },
  Asr: { en: 'Asr', hr: 'Ikindija', icon: '🌤️' },
  Maghrib: { en: 'Maghrib', hr: 'Akšam', icon: '🌅' },
  Isha: { en: 'Isha', hr: 'Jacija', icon: '🌙' },
};

const RECURRENCE_OPTIONS = [
  { value: '', label_en: 'No repeat', label_hr: 'Bez ponavljanja' },
  { value: 'FREQ=DAILY', label_en: 'Daily', label_hr: 'Dnevno' },
  { value: 'FREQ=WEEKLY', label_en: 'Weekly', label_hr: 'Tjedno' },
  { value: 'FREQ=MONTHLY', label_en: 'Monthly', label_hr: 'Mjesečno' },
];

// Sarajevo timezone offset (CET/CEST)
const getSarajevoTimezoneOffset = (date) => {
  // CET is UTC+1, CEST is UTC+2 (last Sunday of March to last Sunday of October)
  const year = date.getFullYear();
  const marchLastSunday = new Date(year, 2, 31);
  marchLastSunday.setDate(31 - marchLastSunday.getDay());
  const octoberLastSunday = new Date(year, 9, 31);
  octoberLastSunday.setDate(31 - octoberLastSunday.getDay());
  
  if (date >= marchLastSunday && date < octoberLastSunday) {
    return 2; // CEST (Summer time) - UTC+2
  }
  return 1; // CET (Winter time) - UTC+1
};

// Convert local time string to ISO - store as UTC so display is correct
const localTimeToISO = (dateStr, timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const [year, month, day] = String(dateStr).split('-').map(Number);
  return new Date(Date.UTC(year, (month || 1) - 1, day || 1, hours || 0, minutes || 0, 0, 0)).toISOString();
};

const getLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildFallbackTodayPlan = (date) => createDefaultTodayPlan(date);
const ensureTodayPlanHasGoal = (plan, date) => ((plan?.priorities?.length || 0) > 0 ? plan : buildFallbackTodayPlan(date));

const parseLocalDateKey = (dateStr) => {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

// Helper: get start of week (Monday-based for European locale)
const getWeekStart = (dateStr) => {
  const d = parseLocalDateKey(dateStr);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
  return new Date(d.setDate(diff));
};

// Helper: get UTC hour from ISO time string (events stored as UTC)
const getEventHour = (timeStr) => {
  if (!timeStr) return 0;
  const d = new Date(timeStr);
  return d.getUTCHours();
};

// Helper: format UTC time for display
const formatTimeUTC = (timeStr) => {
  if (!timeStr) return '';
  const d = new Date(timeStr);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
};

/* ===== DAY VIEW COMPONENT ===== */
function DayViewGrid({ selectedDate, todayEvents, getEventTypeInfo, openEditModal, formatTime, setNewEvent, setShowModal, lang }) {
  const scrollRef = useRef(null);
  const currentHour = new Date().getHours();
  const isToday = selectedDate === getLocalDateKey();
  const HOUR_H = 60; // pixels per hour

  useEffect(() => {
    if (scrollRef.current) {
      const scrollTo = isToday ? Math.max(currentHour - 1, 0) : 5;
      scrollRef.current.scrollTop = scrollTo * HOUR_H;
    }
  }, [selectedDate]);

  // Calculate position & height for each event, then assign columns for overlaps
  const positioned = todayEvents.map(event => {
    const s = new Date(event.start_time);
    const e = event.end_time ? new Date(event.end_time) : new Date(s.getTime() + 30 * 60000);
    const startMin = s.getUTCHours() * 60 + s.getUTCMinutes();
    const endMin = e.getUTCHours() * 60 + e.getUTCMinutes();
    const durationMin = Math.max(endMin - startMin, 15);
    return { ...event, topPx: startMin, heightPx: durationMin, startMin, endMin: startMin + durationMin, durationMin, col: 0, totalCols: 1 };
  }).sort((a, b) => a.startMin - b.startMin);

  // Simple overlap grouping
  const groups = [];
  for (const ev of positioned) {
    let placed = false;
    for (const g of groups) {
      if (ev.startMin < g.maxEnd) {
        g.items.push(ev);
        g.maxEnd = Math.max(g.maxEnd, ev.endMin);
        placed = true;
        break;
      }
    }
    if (!placed) groups.push({ items: [ev], maxEnd: ev.endMin });
  }
  for (const g of groups) {
    const cols = [];
    for (const ev of g.items) {
      let col = 0;
      while (cols[col] && cols[col] > ev.startMin) col++;
      ev.col = col;
      cols[col] = ev.endMin;
    }
    const totalCols = cols.length;
    for (const ev of g.items) ev.totalCols = totalCols;
  }

  return (
    <div ref={scrollRef} className="relative overflow-y-auto max-h-[600px]" data-testid="day-view-grid">
      <div className="relative flex" style={{ height: 24 * HOUR_H }}>
        {/* Time labels column */}
        <div className="w-16 sm:w-20 flex-shrink-0 relative">
          {Array.from({ length: 24 }, (_, hour) => {
            const isCurrentHour = currentHour === hour && isToday;
            return (
              <div
                key={hour}
                className={`absolute left-0 right-0 border-r border-b border-stone-100 px-2 py-1.5 text-right ${
                  isCurrentHour ? 'text-blue-600 font-semibold bg-blue-50/40' : 'text-stone-400'
                }`}
                style={{ top: hour * HOUR_H, height: HOUR_H }}
              >
                <span className="text-xs sm:text-sm">{hour.toString().padStart(2, '0')}:00</span>
              </div>
            );
          })}
        </div>

        {/* Events column */}
        <div className="flex-1 relative">
          {/* Hour grid lines + click areas */}
          {Array.from({ length: 24 }, (_, hour) => {
            const isCurrentHour = currentHour === hour && isToday;
            return (
              <button
                key={hour}
                onClick={() => {
                  const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                  setNewEvent(prev => ({
                    ...prev,
                    start_time: `${selectedDate}T${timeStr}`,
                    end_time: `${selectedDate}T${((hour + 1) % 24).toString().padStart(2, '0')}:00`
                  }));
                  setShowModal(true);
                }}
                className={`absolute left-0 right-0 border-b border-stone-100 hover:bg-stone-50/50 transition-colors ${isCurrentHour ? 'bg-blue-50/40' : ''}`}
                style={{ top: hour * HOUR_H, height: HOUR_H }}
                data-testid={`day-slot-${hour}`}
              />
            );
          })}

          {/* Current time indicator */}
          {isToday && (() => {
            const now = new Date();
            const nowMin = now.getHours() * 60 + now.getMinutes();
            return (
              <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: nowMin }}>
                <div className="relative h-0.5 bg-red-500">
                  <div className="absolute left-0 w-2.5 h-2.5 bg-red-500 rounded-full -translate-y-1/2" />
                </div>
              </div>
            );
          })()}

          {/* Events */}
          {positioned.map(event => {
            const typeInfo = getEventTypeInfo(event.event_type);
            const startH = Math.floor(event.startMin / 60);
            const startM = event.startMin % 60;
            const endTotal = event.startMin + event.durationMin;
            const endH = Math.floor(endTotal / 60);
            const endM = endTotal % 60;
            const timeLabel = `${startH.toString().padStart(2,'0')}:${startM.toString().padStart(2,'0')} – ${endH.toString().padStart(2,'0')}:${endM.toString().padStart(2,'0')}`;
            const widthPct = (100 / event.totalCols) - 1;
            const leftPct = event.col * (100 / event.totalCols);
            return (
              <div
                key={event.event_id || event.id}
                onClick={() => openEditModal(event)}
                data-testid={`day-event-${event.event_id || event.id}`}
                className={`absolute ${typeInfo.color} text-white rounded-lg cursor-pointer hover:brightness-110 hover:shadow-lg transition-all z-10 overflow-hidden ${
                  event.completed ? 'opacity-50' : ''
                }`}
                style={{
                  top: event.topPx + 1,
                  height: Math.max(event.heightPx - 2, 18),
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                }}
                title={`${event.title} (${timeLabel})`}
              >
                <div className="px-2 py-1 h-full flex flex-col justify-start">
                  <span className={`font-medium truncate ${event.heightPx > 30 ? 'text-sm' : 'text-xs'} ${event.completed ? 'line-through' : ''}`}>
                    {event.title}
                  </span>
                  {event.heightPx > 30 && (
                    <span className="text-white/70 text-xs">{timeLabel}</span>
                  )}
                  {event.completed && <Check className="w-3 h-3 flex-shrink-0 mt-0.5" />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ===== WEEK VIEW COMPONENT - Outlook Style ===== */
function WeekViewGrid({ selectedDate, events, getEventTypeInfo, openEditModal, formatTime, setSelectedDate, setViewMode, lang }) {
  const scrollRef = useRef(null);
  const weekStart = getWeekStart(selectedDate);
  const todayStr = getLocalDateKey();
  const currentHour = new Date().getHours();
  const HOUR_H = 48; // pixels per hour in week view
  const START_HOUR = 4; // start from 4 AM
  const TOTAL_HOURS = 20; // 4 AM to midnight
  
  const dayNames = lang === 'hr' 
    ? ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return getLocalDateKey(d);
  });

  useEffect(() => {
    if (scrollRef.current) {
      const isThisWeek = weekDays.includes(todayStr);
      const scrollTo = isThisWeek ? Math.max(currentHour - START_HOUR - 1, 0) : 2;
      scrollRef.current.scrollTop = scrollTo * HOUR_H;
    }
  }, [selectedDate]);

  // Group events by day
  const eventsByDay = {};
  weekDays.forEach(dayStr => {
    const dayEvents = events.filter(e => e.start_time?.split('T')[0] === dayStr);
    // Position each event
    const positioned = dayEvents.map(event => {
      const s = new Date(event.start_time);
      const e = event.end_time ? new Date(event.end_time) : new Date(s.getTime() + 30 * 60000);
      const startMin = s.getUTCHours() * 60 + s.getUTCMinutes() - START_HOUR * 60;
      const endMin = e.getUTCHours() * 60 + e.getUTCMinutes() - START_HOUR * 60;
      const durationMin = Math.max(endMin - startMin, 12);
      return { ...event, topPx: Math.max(startMin, 0) * (HOUR_H / 60), heightPx: durationMin * (HOUR_H / 60), startMin, endMin: startMin + durationMin, durationMin, col: 0, totalCols: 1 };
    }).sort((a, b) => a.startMin - b.startMin);

    // Overlap grouping
    const groups = [];
    for (const ev of positioned) {
      let placed = false;
      for (const g of groups) {
        if (ev.startMin < g.maxEnd) {
          g.items.push(ev);
          g.maxEnd = Math.max(g.maxEnd, ev.endMin);
          placed = true;
          break;
        }
      }
      if (!placed) groups.push({ items: [ev], maxEnd: ev.endMin });
    }
    for (const g of groups) {
      const cols = [];
      for (const ev of g.items) {
        let col = 0;
        while (cols[col] && cols[col] > ev.startMin) col++;
        ev.col = col;
        cols[col] = ev.endMin;
      }
      const totalCols = cols.length;
      for (const ev of g.items) ev.totalCols = totalCols;
    }
    eventsByDay[dayStr] = positioned;
  });

  return (
    <div data-testid="week-view-grid">
      {/* Sticky day headers */}
      <div className="grid border-b border-stone-200 bg-stone-50 sticky top-0 z-20" style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}>
        <div className="border-r border-stone-200 p-1" />
        {weekDays.map((dayStr, i) => {
          const d = parseLocalDateKey(dayStr);
          const isToday = dayStr === todayStr;
          return (
            <button
              key={dayStr}
              onClick={() => { setSelectedDate(dayStr); setViewMode('day'); }}
              className={`p-2 text-center border-r border-stone-200 last:border-r-0 transition-colors hover:bg-stone-100 ${
                isToday ? 'bg-primary/10' : ''
              }`}
              data-testid={`week-day-header-${i}`}
            >
              <p className={`text-xs font-medium ${isToday ? 'text-primary' : 'text-stone-500'}`}>{dayNames[i]}</p>
              <p className={`text-base font-bold ${isToday ? 'text-white bg-primary w-8 h-8 rounded-full flex items-center justify-center mx-auto' : 'text-stone-700'}`}>
                {d.getDate()}
              </p>
            </button>
          );
        })}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="overflow-y-auto max-h-[550px]">
        <div className="grid" style={{ gridTemplateColumns: '56px repeat(7, 1fr)', height: TOTAL_HOURS * HOUR_H }}>
          {/* Time labels column */}
          <div className="relative border-r border-stone-100">
            {Array.from({ length: TOTAL_HOURS }, (_, i) => {
              const hour = i + START_HOUR;
              const isCurrentHour = currentHour === hour && weekDays.includes(todayStr);
              return (
                <div
                  key={hour}
                  className={`absolute left-0 right-0 border-b border-stone-100 px-1 py-1 text-right ${
                    isCurrentHour ? 'text-blue-600 font-semibold' : 'text-stone-400'
                  }`}
                  style={{ top: i * HOUR_H, height: HOUR_H }}
                >
                  <span className="text-xs">{hour.toString().padStart(2, '0')}:00</span>
                </div>
              );
            })}
          </div>

          {/* Day columns with events */}
          {weekDays.map((dayStr, dayIdx) => {
            const isToday = dayStr === todayStr;
            const dayPositioned = eventsByDay[dayStr] || [];
            return (
              <div key={dayStr} className="relative border-r border-stone-100 last:border-r-0">
                {/* Hour grid lines */}
                {Array.from({ length: TOTAL_HOURS }, (_, i) => {
                  const hour = i + START_HOUR;
                  const isNow = isToday && currentHour === hour;
                  return (
                    <div
                      key={hour}
                      className={`absolute left-0 right-0 border-b border-stone-100 ${isNow ? 'bg-blue-50/50' : ''}`}
                      style={{ top: i * HOUR_H, height: HOUR_H }}
                    />
                  );
                })}

                {/* Current time indicator */}
                {isToday && (() => {
                  const now = new Date();
                  const nowMin = (now.getHours() - START_HOUR) * 60 + now.getMinutes();
                  if (nowMin < 0) return null;
                  return (
                    <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: nowMin * (HOUR_H / 60) }}>
                      <div className="h-0.5 bg-red-500">
                        <div className="absolute left-0 w-2 h-2 bg-red-500 rounded-full -translate-y-1/2" />
                      </div>
                    </div>
                  );
                })()}

                {/* Events */}
                {dayPositioned.map(event => {
                  const typeInfo = getEventTypeInfo(event.event_type);
                  const widthPct = (100 / event.totalCols) - 2;
                  const leftPct = event.col * (100 / event.totalCols);
                  return (
                    <div
                      key={event.event_id || event.id}
                      onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                      data-testid={`week-event-${event.event_id || event.id}`}
                      className={`absolute ${typeInfo.color} text-white rounded cursor-pointer hover:brightness-110 hover:shadow-md transition-all z-10 overflow-hidden ${
                        event.completed ? 'opacity-50' : ''
                      }`}
                      style={{
                        top: event.topPx + 1,
                        height: Math.max(event.heightPx - 2, 14),
                        left: `${leftPct + 1}%`,
                        width: `${widthPct}%`,
                      }}
                      title={`${event.title} - ${formatTimeUTC(event.start_time)}`}
                    >
                      <div className="px-1 py-0.5 h-full">
                        <span className="text-[10px] font-medium leading-tight block truncate">{event.title}</span>
                        {event.heightPx > 20 && (
                          <span className="text-[9px] text-white/70 block">{formatTimeUTC(event.start_time)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ===== MONTH VIEW COMPONENT ===== */
function MonthViewGrid({ selectedDate, events, getEventTypeInfo, getIslamicEventForDate, setSelectedDate, setViewMode, formatTime, lang }) {
  const todayStr = getLocalDateKey();
  
  const dayNames = lang === 'hr' 
    ? ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const firstDay = parseLocalDateKey(selectedDate);
  firstDay.setDate(1);
  const lastDay = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0);
  // Monday-based start: 0=Mon, 6=Sun
  let startPadding = firstDay.getDay() - 1;
  if (startPadding < 0) startPadding = 6;
  const totalDays = lastDay.getDate();

  // Previous month days for padding
  const prevMonthLast = new Date(firstDay.getFullYear(), firstDay.getMonth(), 0);

  return (
    <div data-testid="month-view-grid">
      {/* Days of week header */}
      <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50">
        {dayNames.map(day => (
          <div key={day} className="p-2 text-center text-xs font-semibold text-stone-500 uppercase tracking-wider border-r border-stone-200 last:border-r-0">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {/* Previous month padding cells */}
        {Array.from({ length: startPadding }, (_, i) => {
          const dayNum = prevMonthLast.getDate() - startPadding + 1 + i;
          return (
            <div key={`prev-${i}`} className="border-r border-b border-stone-100 p-1.5 min-h-[85px] bg-stone-50/30">
              <span className="text-xs text-stone-300">{dayNum}</span>
            </div>
          );
        })}
        
        {/* Current month day cells */}
        {Array.from({ length: totalDays }, (_, idx) => {
          const day = idx + 1;
          const dateStr = `${firstDay.getFullYear()}-${(firstDay.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
          const dayEvents = events.filter(e => e.start_time?.split('T')[0] === dateStr);
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          const islamicEvent = getIslamicEventForDate(dateStr);
          
          return (
            <div 
              key={day}
              onClick={() => { setSelectedDate(dateStr); setViewMode('day'); }}
              data-testid={`month-day-${day}`}
              className={`border-r border-b border-stone-100 p-1.5 min-h-[85px] cursor-pointer transition-all hover:bg-primary/5 ${
                isToday ? 'bg-primary/10 ring-1 ring-primary/30 ring-inset' : ''
              } ${isSelected ? 'bg-primary/5' : ''}`}
            >
              <div className="flex items-start justify-between mb-1">
                <span className={`text-sm leading-none ${
                  isToday 
                    ? 'text-white bg-primary w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs' 
                    : 'text-stone-700 font-medium'
                }`}>
                  {day}
                </span>
                {dayEvents.length > 0 && !isToday && (
                  <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1" />
                )}
              </div>
              
              {islamicEvent && (
                <div className={`text-[10px] leading-tight px-1 py-0.5 rounded mb-0.5 truncate font-medium ${
                  islamicEvent.type === 'eid' ? 'bg-green-100 text-green-700' :
                  islamicEvent.type === 'ramadan' ? 'bg-emerald-100 text-emerald-700' :
                  islamicEvent.type === 'holy' ? 'bg-purple-100 text-purple-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {lang === 'hr' ? islamicEvent.name_hr : islamicEvent.name_en}
                </div>
              )}
              
              <div className="space-y-0.5">
                {dayEvents.slice(0, 2).map(event => {
                  const typeInfo = getEventTypeInfo(event.event_type);
                  return (
                    <div
                      key={event.event_id || event.id}
                      className={`${typeInfo.color} text-white text-[10px] leading-tight px-1 py-0.5 rounded truncate`}
                      onClick={(e) => { e.stopPropagation(); }}
                      title={`${event.title} - ${formatTimeUTC(event.start_time)}`}
                    >
                      <span className="opacity-80">{formatTimeUTC(event.start_time)}</span> {event.title}
                    </div>
                  );
                })}
                {dayEvents.length > 2 && (
                  <p className="text-[10px] text-stone-500 font-medium pl-1">+{dayEvents.length - 2} {lang === 'hr' ? 'više' : 'more'}</p>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Next month padding to fill the row */}
        {Array.from({ length: (7 - ((startPadding + totalDays) % 7)) % 7 }, (_, i) => (
          <div key={`next-${i}`} className="border-r border-b border-stone-100 p-1.5 min-h-[85px] bg-stone-50/30">
            <span className="text-xs text-stone-300">{i + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Planner({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  const [events, setEvents] = useState([]);
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [prayerLog, setPrayerLog] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getLocalDateKey());
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [customActivities, setCustomActivities] = useState([]);
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month', 'timeline'
  const [showPrayerTimes, setShowPrayerTimes] = useState(true);
  const [todayPlan, setTodayPlan] = useState(() => buildFallbackTodayPlan(getLocalDateKey()));
  const [timelineData, setTimelineData] = useState({ planned: [], actual: [], mergedTimeline: [] });
  const [savingTodayPlan, setSavingTodayPlan] = useState(false);
  const [blockingItemId, setBlockingItemId] = useState('');
  const [planExpanded, setPlanExpanded] = useState(true);
  const [pendingRemovedBlockedEvents, setPendingRemovedBlockedEvents] = useState([]);
  const {
    initializePriorityTimeState,
    normalizePriorityFieldValue,
    registerPendingEndTimeNormalization,
  } = usePlannerPriorityTimeNormalization({ plan: todayPlan, setPlan: setTodayPlan });
  
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    event_type: 'general',
    is_recurring: false,
    recurrence_rule: '',
    reminder_minutes: null,
    bereket_activity_id: null
  });
  const goalSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const fetchPrayerTimes = async () => {
    try {
      const settings = await getUserPrayerSettings();
      const city = settings.default_city || 'Sarajevo';
      const country = settings.default_country || 'Bosnia and Herzegovina';

      const dateObj = parseLocalDateKey(selectedDate);
      const day = dateObj.getDate();
      const month = dateObj.getMonth() + 1;
      const year = dateObj.getFullYear();

      const data = await getPrayerTimes(city, country, day, month, year);

      if (data?.timings) {
        const timings = { ...data.timings };
        const adjustments = {
          fajr: settings.fajr_adjustment || 0,
          dhuhr: settings.dhuhr_adjustment || 0,
          asr: settings.asr_adjustment || 0,
          maghrib: settings.maghrib_adjustment || 0,
          isha: settings.isha_adjustment || 0
        };

        Object.keys(adjustments).forEach(prayer => {
          if (timings[prayer] && adjustments[prayer]) {
            const [hours, minutes] = timings[prayer].split(':').map(Number);
            const totalMinutes = hours * 60 + minutes + adjustments[prayer];
            const newHours = Math.floor(totalMinutes / 60) % 24;
            const newMinutes = totalMinutes % 60;
            timings[prayer] = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
          }
        });

        setPrayerTimes(timings);
      }
    } catch (error) {
      console.error('Error fetching prayer times:', error);
      try {
        const dateObj = parseLocalDateKey(selectedDate);
        const data = await getPrayerTimes('Sarajevo', 'Bosnia and Herzegovina', dateObj.getDate(), dateObj.getMonth() + 1, dateObj.getFullYear());
        if (data?.timings) {
          setPrayerTimes(data.timings);
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  };

  const fetchPrayerLog = async () => {
    try {
      const data = await getPrayersToday();
      setPrayerLog({
        completed_prayers: Object.keys(data).filter(key => data[key] && key !== 'id' && key !== 'user_id' && key !== 'date' && key !== 'created_at')
      });
    } catch (error) {
      console.error('Error fetching prayer log:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      let startDate = selectedDate;
      let endDate = selectedDate;

      if (viewMode === 'week') {
        const start = getWeekStart(selectedDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        startDate = getLocalDateKey(start);
        endDate = getLocalDateKey(end);
      } else if (viewMode === 'month') {
        const start = parseLocalDateKey(selectedDate);
        start.setDate(1);
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        startDate = getLocalDateKey(start);
        endDate = getLocalDateKey(end);
      }

      const data = await getPlannerEvents(`${startDate}T00:00:00`, `${endDate}T23:59:59`);
      setEvents(data.map(event => ({
        ...event,
        event_id: event.id
      })));
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchCustomActivities = async () => {
    try {
      const data = await getCustomActivities();
      setCustomActivities(data || []);
    } catch (error) {
      console.error('Error fetching custom activities:', error);
    }
  };

  const fetchTodayPlan = async () => {
    const optimisticPlan = buildFallbackTodayPlan(selectedDate);
    setTodayPlan(optimisticPlan);
    initializePriorityTimeState(optimisticPlan.priorities);
    setPlanExpanded(true);

    try {
      const data = await getTodayBarakahPlan(selectedDate);
      const normalizedPlan = ensureTodayPlanHasGoal(data, selectedDate);
      setTodayPlan(normalizedPlan);
      initializePriorityTimeState(normalizedPlan?.priorities || []);
      setPendingRemovedBlockedEvents([]);

      // Auto-collapse if all current goals have titles
      const priorityCount = normalizedPlan?.priorities?.length || 0;
      const allFilled = priorityCount > 0 && (normalizedPlan?.priorities || []).every(p => p?.title?.trim());
      if (allFilled) setPlanExpanded(false);
      else setPlanExpanded(true);
    } catch (error) {
      console.error('Error fetching today plan:', error);
      const fallbackPlan = buildFallbackTodayPlan(selectedDate);
      setTodayPlan(fallbackPlan);
      initializePriorityTimeState(fallbackPlan.priorities);
      setPendingRemovedBlockedEvents([]);
      setPlanExpanded(true);
    }
  };

  const fetchTimelineData = async () => {
    try {
      const data = await getPlannedVsActualTimeline(selectedDate);
      setTimelineData(data || { planned: [], actual: [], mergedTimeline: [] });
    } catch (error) {
      console.error('Error fetching planned vs actual timeline:', error);
      setTimelineData({ planned: [], actual: [], mergedTimeline: [] });
    }
  };

  useEffect(() => {
    fetchCustomActivities();
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchPrayerTimes();
    fetchTodayPlan();
    fetchTimelineData();
    if (viewMode === 'day') {
      fetchPrayerLog();
    }
  }, [selectedDate, viewMode]);

  const isPrayerCompleted = (prayerName) => {
    return prayerLog?.completed_prayers?.includes(prayerName.toLowerCase());
  };

  const togglePrayer = async (prayerName) => {
    try {
      const isCompleted = isPrayerCompleted(prayerName);
      await updatePrayer(prayerName.toLowerCase(), !isCompleted);
      toast.success(
        isCompleted
          ? (lang === 'hr' ? `${prayerName} uklonjen` : `${prayerName} removed`)
          : (lang === 'hr' ? `${prayerName} obavljen!` : `${prayerName} completed!`)
      );
      fetchPrayerLog();
    } catch (error) {
      console.error('Error toggling prayer:', error);
    }
  };

  const updateTodayPlanPriority = (index, field, value) => {
    const normalizedValue = normalizePriorityFieldValue(field, value);
    setTodayPlan((prev) => {
      if (!prev) return prev;
      const priorities = [...(prev.priorities || [])];
      const current = priorities[index] || {};
      let nextPriority = { ...current, [field]: normalizedValue };

      priorities[index] = nextPriority;
      if (field === 'start_time' && normalizedValue) {
        const cur = priorities[index];
        if (cur.end_time && normalizedValue >= cur.end_time) {
          const [h, m] = normalizedValue.split(':').map(Number);
          const totalMin = (h || 0) * 60 + (m || 0) + 60;
          priorities[index] = { ...cur, start_time: normalizedValue, end_time: `${String(Math.floor(totalMin / 60) % 24).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}` };
        }
      }
      return { ...prev, priorities };
    });
  };

  const addTodayPlanGoal = () => {
    setTodayPlan((prev) => {
      const current = prev || buildFallbackTodayPlan(selectedDate);
      const nextPriorities = [...(current.priorities || []), createEmptyTodayPlanPriority(current.priorities?.length || 0)];
      initializePriorityTimeState(nextPriorities);
      return { ...current, priorities: nextPriorities };
    });
  };

  const reorderTodayPlanGoals = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setTodayPlan((prev) => {
      if (!prev) return prev;

      const priorities = [...(prev.priorities || [])];
      if (!priorities[fromIndex] || !priorities[toIndex]) return prev;

      const reorderedPriorities = arrayMove(priorities, fromIndex, toIndex);
      initializePriorityTimeState(reorderedPriorities);
      return { ...prev, priorities: reorderedPriorities };
    });
  };

  const removeTodayPlanGoal = (index) => {
    setTodayPlan((prev) => {
      if (!prev) return prev;
      const priorities = [...(prev.priorities || [])];
      if (priorities.length <= 1) return prev;

      const removed = priorities[index];
      if (removed?.blocked_event_id) {
        setPendingRemovedBlockedEvents((current) => [...new Set([...current, removed.blocked_event_id])]);
      }

      const nextPriorities = priorities.filter((_, priorityIndex) => priorityIndex !== index);
      initializePriorityTimeState(nextPriorities);
      return { ...prev, priorities: nextPriorities };
    });
  };

  const handleGoalDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const priorities = todayPlan?.priorities || [];
    const oldIndex = priorities.findIndex((item) => item.id === active.id);
    const newIndex = priorities.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;
    reorderTodayPlanGoals(oldIndex, newIndex);
  };

  const saveTodayPlanHandler = async () => {
    if (!todayPlan) return;

    const validation = validatePriorityRanges(todayPlan?.priorities || []);
    if (validation.hasInvalidRanges) {
      toast.error(lang === 'hr' ? 'Ako postaviš vrijeme, ispuni oba polja i završetak mora biti nakon početka.' : 'If you set a time, fill both fields and keep the end after the start.');
      return;
    }

    if (validation.hasSequenceConflicts) {
      const confirmed = window.confirm(
        lang === 'hr'
          ? 'Otkriveno je preklapanje ili nelogičan redoslijed ciljeva. Želiš li ipak spremiti plan?'
          : 'An overlap or non-sequential goal order was detected. Do you still want to save the plan?'
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setSavingTodayPlan(true);
      const saved = await saveTodayBarakahPlan(todayPlan, selectedDate);
      if (pendingRemovedBlockedEvents.length > 0) {
        await Promise.allSettled(pendingRemovedBlockedEvents.map((eventId) => deletePlannerEvent(eventId)));
      }
      const normalizedSavedPlan = ensureTodayPlanHasGoal(saved, selectedDate);
      setTodayPlan(normalizedSavedPlan);
      setPendingRemovedBlockedEvents([]);
      toast.success(lang === 'hr' ? 'Današnji plan je spremljen.' : 'Today plan saved.');
      fetchTimelineData();
      fetchEvents();
    } catch (error) {
      console.error('Error saving today plan:', error);
      toast.error(lang === 'hr' ? 'Greška pri spremanju plana' : 'Failed to save today plan');
    } finally {
      setSavingTodayPlan(false);
    }
  };

  const blockPriorityInCalendar = async (priority, index) => {
    if (!priority?.title?.trim()) {
      toast.error(lang === 'hr' ? 'Prvo unesi naziv cilja.' : 'Enter a goal title first.');
      return;
    }

    if (!hasPriorityTimeRange(priority)) {
      toast.error(lang === 'hr' ? 'Dodaj početak i kraj vremena prije spremanja u kalendar.' : 'Add both start and end time before blocking in calendar.');
      return;
    }

    const itemId = priority.id || `priority-${index + 1}`;
    const startTime = priority.start_time || '09:00';
    const endTime = priority.end_time || '10:00';
    const validation = validatePriorityRanges(todayPlan?.priorities || []);

    if (validation.hasInvalidRanges) {
      toast.error(lang === 'hr' ? 'Provjeri vremena ciljeva prije blokiranja u kalendaru.' : 'Review goal times before blocking in calendar.');
      return;
    }

    const priorityConflict = validation.sequenceConflicts.some((conflict) => (
      conflict.previousIndex === index || conflict.currentIndex === index
    ));

    if (priorityConflict) {
      const confirmed = window.confirm(
        lang === 'hr'
          ? 'Ovaj cilj se vremenski preklapa ili remeti redoslijed ostalih ciljeva. Želiš li ga ipak blokirati u kalendaru?'
          : 'This goal overlaps or breaks the goal order. Do you still want to block it in the calendar?'
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setBlockingItemId(itemId);
      const event = await createPlanItemCalendarBlock({
        date: selectedDate,
        itemId,
        title: priority.title,
        startTime,
        endTime,
      });

      const updatedPlan = {
        ...todayPlan,
        priorities: (todayPlan?.priorities || []).map((item, idx) => (
          idx === index
            ? {
              ...item,
              id: item.id || itemId,
              blocked_event_id: event?.id || null,
              blocked_start_time: event?.start_time || null,
            }
            : item
        )),
      };

      const saved = await saveTodayBarakahPlan(updatedPlan, selectedDate);
      setTodayPlan(saved);
      toast.success(lang === 'hr' ? 'Cilj je blokiran u kalendaru.' : 'Goal blocked in calendar.');
      fetchEvents();
      fetchTimelineData();
    } catch (error) {
      console.error('Error blocking priority:', error);
      toast.error(lang === 'hr' ? 'Blokiranje nije uspjelo.' : 'Failed to block time.');
    } finally {
      setBlockingItemId('');
    }
  };

  const addPrayersToPlanner = async (period = 'day') => {
    if (!prayerTimes && period === 'day') {
      toast.error(lang === 'hr' ? 'Vaktija nije učitana' : 'Prayer times not loaded');
      return;
    }

    const prayersToAdd = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
    const prayerLabels = {
      Fajr: { en: 'Fajr Prayer', hr: 'Sabah namaz' },
      Dhuhr: { en: 'Dhuhr Prayer', hr: 'Podne namaz' },
      Asr: { en: 'Asr Prayer', hr: 'Ikindija namaz' },
      Maghrib: { en: 'Maghrib Prayer', hr: 'Akšam namaz' },
      Isha: { en: 'Isha Prayer', hr: 'Jacija namaz' },
    };

    let addedCount = 0;
    let dates = [];
    
    // Calculate dates based on period
    const startDate = parseLocalDateKey(selectedDate);
    if (period === 'day') {
      dates = [selectedDate];
    } else if (period === 'week') {
      for (let i = 0; i < 7; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        dates.push(getLocalDateKey(d));
      }
    } else if (period === 'month') {
      for (let i = 0; i < 30; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        dates.push(getLocalDateKey(d));
      }
    } else if (period === 'year') {
      for (let i = 0; i < 365; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        dates.push(getLocalDateKey(d));
      }
    }

    // Show loading toast for bulk operations
    if (dates.length > 1) {
      toast.loading(
        lang === 'hr' 
          ? `Dodajem namaze za ${dates.length} dana...` 
          : `Adding prayers for ${dates.length} days...`,
        { id: 'bulk-prayer-loading' }
      );
    }

    const settings = await getUserPrayerSettings();
    const city = settings.default_city || 'Sarajevo';
    const country = settings.default_country || 'Bosnia and Herzegovina';

    for (const dateStr of dates) {
      let timingsForDate = prayerTimes;
      
      // For bulk operations, fetch prayer times for each date
      if (period !== 'day') {
        try {
          const dateObj = parseLocalDateKey(dateStr);
          const data = await getPrayerTimes(city, country, dateObj.getDate(), dateObj.getMonth() + 1, dateObj.getFullYear());
          if (data?.timings) {
            timingsForDate = { ...data.timings };
            // Apply adjustments
            const adjustments = {
              fajr: settings.fajr_adjustment || 0,
              dhuhr: settings.dhuhr_adjustment || 0,
              asr: settings.asr_adjustment || 0,
              maghrib: settings.maghrib_adjustment || 0,
              isha: settings.isha_adjustment || 0
            };
            Object.keys(adjustments).forEach(prayer => {
              if (timingsForDate[prayer] && adjustments[prayer]) {
                const [hours, minutes] = timingsForDate[prayer].split(':').map(Number);
                const totalMinutes = hours * 60 + minutes + adjustments[prayer];
                const newHours = Math.floor(totalMinutes / 60) % 24;
                const newMinutes = totalMinutes % 60;
                timingsForDate[prayer] = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
              }
            });
          }
        } catch (err) {
          console.error(`Error fetching prayer times for ${dateStr}:`, err);
          continue;
        }
      }

      if (!timingsForDate) continue;

      for (const prayer of prayersToAdd) {
        const timeKey = prayer.toLowerCase();
        const prayerTime = timingsForDate[timeKey];

        if (!prayerTime) continue;

        // Use timezone-aware ISO string
        const isoTime = localTimeToISO(dateStr, prayerTime);

        const eventData = {
          title: lang === 'hr' ? prayerLabels[prayer].hr : prayerLabels[prayer].en,
          description: lang === 'hr' ? 'Dnevni namaz' : 'Daily prayer',
          start_time: isoTime,
          end_time: isoTime,
          event_type: 'prayer',
          is_recurring: false,
          recurrence_rule: '',
          reminder_minutes: 15,
          bereket_activity_id: null
        };

        try {
          await createPlannerEvent(eventData);
          addedCount++;
        } catch (error) {
          console.error(`Error adding ${prayer} for ${dateStr}:`, error);
        }
      }
    }

    // Dismiss loading toast
    if (dates.length > 1) {
      toast.dismiss('bulk-prayer-loading');
    }

    if (addedCount > 0) {
      toast.success(
        lang === 'hr'
          ? `${addedCount} namaza dodano u planer!`
          : `${addedCount} prayers added to planner!`
      );
      fetchEvents();
    } else {
      toast.error(lang === 'hr' ? 'Greška pri dodavanju namaza' : 'Error adding prayers');
    }
  };

  // Delete duplicate prayer events (keeps only the newest one for each prayer on each date)
  const cleanupDuplicatePrayers = async () => {
    try {
      toast.loading(
        lang === 'hr' ? 'Čistim duplikate...' : 'Cleaning duplicates...',
        { id: 'cleanup-loading' }
      );

      // Get all events
      const allEvents = await getPlannerEvents('2020-01-01T00:00:00', '2030-12-31T23:59:59');
      const prayerEvents = allEvents.filter(e => e.event_type === 'prayer');

      // Group by date and title
      const grouped = {};
      prayerEvents.forEach(event => {
        const dateKey = event.start_time.split('T')[0];
        const key = `${dateKey}-${event.title}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(event);
      });

      // Find duplicates and delete older ones
      let deletedCount = 0;
      for (const key of Object.keys(grouped)) {
        const events = grouped[key];
        if (events.length > 1) {
          // Sort by created_at descending, keep newest
          events.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          // Delete all except the first (newest)
          for (let i = 1; i < events.length; i++) {
            try {
              await deletePlannerEvent(events[i].id);
              deletedCount++;
            } catch (err) {
              console.error('Error deleting duplicate:', err);
            }
          }
        }
      }

      toast.dismiss('cleanup-loading');

      if (deletedCount > 0) {
        toast.success(
          lang === 'hr'
            ? `${deletedCount} duplikata obrisano!`
            : `${deletedCount} duplicates removed!`
        );
        fetchEvents();
      } else {
        toast.info(
          lang === 'hr' ? 'Nema duplikata za brisanje' : 'No duplicates to remove'
        );
      }
    } catch (error) {
      toast.dismiss('cleanup-loading');
      console.error('Error cleaning duplicates:', error);
      toast.error(lang === 'hr' ? 'Greška pri čišćenju' : 'Error cleaning');
    }
  };

  const saveEvent = async () => {
    if (!newEvent.title) {
      toast.error(lang === 'hr' ? 'Dodaj naslov' : 'Add a title');
      return;
    }

    try {
      if (editingEvent) {
        await updatePlannerEvent(editingEvent.event_id, newEvent);
        toast.success(lang === 'hr' ? 'Događaj ažuriran!' : 'Event updated!');
      } else {
        await createPlannerEvent(newEvent);
        toast.success(lang === 'hr' ? 'Događaj dodan!' : 'Event added!');
      }

      setShowModal(false);
      setEditingEvent(null);
      resetNewEvent();
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(lang === 'hr' ? 'Greška pri spremanju' : 'Error saving');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm(lang === 'hr' ? 'Obriši događaj?' : 'Delete event?')) return;

    try {
      await deletePlannerEvent(eventId);
      toast.success(lang === 'hr' ? 'Događaj obrisan!' : 'Event deleted!');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const toggleComplete = async (eventId) => {
    try {
      const event = events.find(e => e.event_id === eventId);
      const newCompletedState = !event?.completed;

      await updatePlannerEvent(eventId, { completed: newCompletedState });

      if (newCompletedState) {
        toast.success(lang === 'hr' ? 'Označeno kao završeno!' : 'Marked complete!');
      } else {
        toast.success(lang === 'hr' ? 'Označeno kao nedovršeno' : 'Marked incomplete');
      }

      fetchEvents();
    } catch (error) {
      console.error('Error toggling complete:', error);
    }
  };

  const exportIcal = async () => {
    try {
      let icalContent = 'BEGIN:VCALENDAR\n';
      icalContent += 'VERSION:2.0\n';
      icalContent += 'PRODID:-//Bereket App//Planner//EN\n';
      icalContent += 'CALSCALE:GREGORIAN\n';
      icalContent += 'METHOD:PUBLISH\n';
      icalContent += 'X-WR-CALNAME:Bereket Planner\n';
      icalContent += 'X-WR-TIMEZONE:UTC\n';

      events.forEach(event => {
        const startDate = new Date(event.start_time);
        const endDate = event.end_time ? new Date(event.end_time) : startDate;

        const formatDate = (date) => {
          return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        icalContent += 'BEGIN:VEVENT\n';
        icalContent += `UID:${event.event_id}@bereket-app\n`;
        icalContent += `DTSTAMP:${formatDate(new Date())}\n`;
        icalContent += `DTSTART:${formatDate(startDate)}\n`;
        icalContent += `DTEND:${formatDate(endDate)}\n`;
        icalContent += `SUMMARY:${event.title}\n`;
        if (event.description) {
          icalContent += `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}\n`;
        }
        icalContent += 'END:VEVENT\n';
      });

      icalContent += 'END:VCALENDAR';

      const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bereket-planner.ics');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(lang === 'hr' ? 'Kalendar izvezen!' : 'Calendar exported!');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error(lang === 'hr' ? 'Greška pri izvozu' : 'Error exporting');
    }
  };

  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      description: '',
      start_time: `${selectedDate}T09:00`,
      end_time: `${selectedDate}T10:00`,
      event_type: 'general',
      is_recurring: false,
      recurrence_rule: '',
      reminder_minutes: null,
      bereket_activity_id: null
    });
  };

  const openNewEventModal = () => {
    resetNewEvent();
    setNewEvent(prev => ({
      ...prev,
      start_time: `${selectedDate}T09:00`,
      end_time: `${selectedDate}T10:00`
    }));
    setShowModal(true);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      description: event.description || '',
      start_time: formatPlannerDateTimeLocalInput(event.start_time),
      end_time: formatPlannerDateTimeLocalInput(event.end_time) || '',
      event_type: event.event_type,
      is_recurring: event.is_recurring,
      recurrence_rule: event.recurrence_rule || '',
      reminder_minutes: event.reminder_minutes,
      bereket_activity_id: event.bereket_activity_id
    });
    setShowModal(true);
  };

  const navigateDate = (direction) => {
    const current = parseLocalDateKey(selectedDate);
    if (viewMode === 'day') {
      current.setDate(current.getDate() + direction);
    } else if (viewMode === 'week') {
      current.setDate(current.getDate() + (direction * 7));
    } else if (viewMode === 'timeline') {
      current.setDate(current.getDate() + direction);
    } else {
      current.setMonth(current.getMonth() + direction);
    }
    setSelectedDate(getLocalDateKey(current));
  };

  const formatDate = (dateStr) => {
    const date = parseLocalDateKey(dateStr);
    return date.toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return formatTimeUTC(timeStr);
  };

  const getEventTypeInfo = (typeId) => EVENT_TYPES.find(t => t.id === typeId) || EVENT_TYPES[5];

  const todayEvents = events.filter(e => e.start_time?.startsWith(selectedDate));
  const plannerHasTodayPlanContent = useMemo(() => hasTodayPlanContent(todayPlan), [todayPlan]);
  const plannerVisibleGoals = useMemo(
    () => (todayPlan?.priorities || []).filter((priority) => (
      String(priority?.title || '').trim() || String(priority?.goal_niyyah || '').trim() || hasPriorityTimeRange(priority)
    )),
    [todayPlan]
  );
  const plannerPriorityWarnings = useMemo(
    () => getPriorityWarningMap(todayPlan?.priorities || []),
    [todayPlan?.priorities]
  );
  const eventsForCurrentView = viewMode === 'day' ? todayEvents : events;

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-accent" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-[#292524]">
                {lang === 'hr' ? 'Bereket Planer' : 'Barakah Planner'}
              </h1>
            </div>
            <p className="text-[#57534E]">
              {lang === 'hr' ? 'Planiraj svoje duhovne aktivnosti' : 'Plan your spiritual activities'}
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={exportIcal}
              className="px-4 py-2 bg-stone-100 text-[#57534E] rounded-lg hover:bg-stone-200 flex items-center gap-2"
              data-testid="planner-export-ical-button"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">iCal</span>
            </button>
            <button
              onClick={openNewEventModal}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
              data-testid="planner-open-new-event-button"
            >
              <Plus className="w-4 h-4" />
              {lang === 'hr' ? 'Novi' : 'New'}
            </button>
          </div>
        </div>

        <div className="mb-6 bg-white/90 backdrop-blur-sm border border-stone-200/60 rounded-2xl shadow-sm overflow-hidden" data-testid="planner-today-plan-card">
          {/* Header - always visible */}
          <div
            className="flex items-center justify-between gap-3 p-4 cursor-pointer hover:bg-stone-50/50 transition-colors"
            onClick={() => setPlanExpanded(prev => !prev)}
            data-testid="planner-today-plan-header"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Star className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-heading font-semibold text-[#292524]" data-testid="planner-today-plan-title">
                  {lang === 'hr' ? 'Nijet + ciljevi' : 'Niyyah + goals'}
                </h2>
                {todayPlan?.niyyah?.trim() && !planExpanded && (
                  <p className="text-xs text-[#78716C] truncate mt-0.5 max-w-[200px] sm:max-w-[400px]">{todayPlan.niyyah}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!planExpanded && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); saveTodayPlanHandler(); }}
                  disabled={savingTodayPlan}
                  className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
                  data-testid="planner-save-today-plan-button-mini"
                >
                  {savingTodayPlan ? '...' : (lang === 'hr' ? 'Spremi' : 'Save')}
                </button>
              )}
              <ChevronRight className={`w-4 h-4 text-stone-400 transition-transform ${planExpanded ? 'rotate-90' : ''}`} />
            </div>
          </div>

          {/* Compact summary - visible when collapsed */}
          {!planExpanded && (
            <div className="px-4 pb-3 space-y-1.5" data-testid="planner-today-plan-summary">
              {plannerHasTodayPlanContent ? plannerVisibleGoals.map((p, idx) => (
                <div key={p.id || idx} className="flex items-center gap-2 text-xs">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    p.blocked_event_id ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'
                  }`}>{idx + 1}</span>
                  <span className={`truncate flex-1 ${p.title?.trim() ? 'text-[#292524] font-medium' : 'text-stone-400 italic'}`}>
                    {p.title?.trim() || (lang === 'hr' ? 'Bez naziva cilja' : 'Untitled goal')}
                  </span>
                  {hasPriorityTimeRange(p) && (
                    <span className="text-[#78716C] font-mono text-[11px] flex-shrink-0">
                      {p.start_time} – {p.end_time}
                    </span>
                  )}
                  {p.blocked_event_id && <Check className="w-3 h-3 text-green-600 flex-shrink-0" />}
                </div>
              )) : (
                <p className="text-xs text-stone-400 italic">
                  {lang === 'hr' ? 'Nijet i cilj još nisu postavljeni.' : 'Niyyah and goal are not set yet.'}
                </p>
              )}
            </div>
          )}

          {/* Expanded form */}
          {planExpanded && (
            <div className="px-5 pb-5 border-t border-stone-100">
              <div className="flex items-center justify-end mt-3 mb-3">
                <button
                  type="button"
                  onClick={saveTodayPlanHandler}
                  disabled={savingTodayPlan}
                  className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                  data-testid="planner-save-today-plan-button"
                >
                  {savingTodayPlan ? (lang === 'hr' ? 'Spremanje...' : 'Saving...') : (lang === 'hr' ? 'Spremi plan' : 'Save plan')}
                </button>
              </div>

              <textarea
                value={todayPlan?.niyyah || ''}
                onChange={(e) => setTodayPlan((prev) => ({ ...(prev || {}), niyyah: e.target.value }))}
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm text-[#292524] resize-none h-16"
                placeholder={lang === 'hr' ? 'Današnji nijet...' : "Today's niyyah..."}
                data-testid="planner-today-plan-niyyah-input"
              />

              <div className="mt-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-[#292524]">
                    {lang === 'hr' ? 'Ciljevi za danas' : 'Goals for today'}
                  </p>
                  <p className="text-xs text-[#78716C] mt-0.5">
                    {lang === 'hr' ? 'Vrijeme je opcionalno. Ako ga dodaš, cilj možeš blokirati u kalendar.' : 'Time is optional. If you add it, you can block the goal in the calendar.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addTodayPlanGoal}
                  className="px-3 py-2 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20"
                  data-testid="planner-today-plan-add-goal-button"
                >
                  {lang === 'hr' ? '+ Dodaj cilj' : '+ Add goal'}
                </button>
              </div>

              <div className="mt-3 space-y-3">
                <DndContext sensors={goalSensors} collisionDetection={closestCenter} onDragEnd={handleGoalDragEnd}>
                  <SortableContext items={(todayPlan?.priorities || []).map((priority) => priority.id)} strategy={verticalListSortingStrategy}>
                    {(todayPlan?.priorities || []).map((priority, idx) => {
                      const itemId = priority.id || `priority-${idx + 1}`;
                      return (
                        <SortableTodayPlanGoalCard
                          key={itemId}
                          goalId={itemId}
                          index={idx}
                          totalGoals={(todayPlan?.priorities || []).length}
                          priority={priority}
                          lang={lang}
                          isBlocking={blockingItemId === itemId}
                          hasTimeRange={hasPriorityTimeRange(priority)}
                          warningState={plannerPriorityWarnings[idx]}
                          onChangeField={(field, value) => updateTodayPlanPriority(idx, field, value)}
                          onRegisterEndNormalization={() => registerPendingEndTimeNormalization(idx)}
                          onBlock={() => blockPriorityInCalendar(priority, idx)}
                          onClearTime={() => {
                            updateTodayPlanPriority(idx, 'start_time', '');
                            updateTodayPlanPriority(idx, 'end_time', '');
                          }}
                          onRemove={() => removeTodayPlanGoal(idx)}
                          onMoveUp={() => reorderTodayPlanGoals(idx, Math.max(idx - 1, 0))}
                          onMoveDown={() => reorderTodayPlanGoals(idx, Math.min(idx + 1, (todayPlan?.priorities || []).length - 1))}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={todayPlan?.avoid_distraction || ''}
                  onChange={(e) => setTodayPlan((prev) => ({ ...(prev || {}), avoid_distraction: e.target.value }))}
                  className="px-3 py-2.5 border border-stone-200 rounded-xl text-sm text-[#292524]"
                  placeholder={lang === 'hr' ? 'Izbjegavati / distrakcije' : 'Avoid / distractions'}
                  data-testid="planner-today-plan-avoid-input"
                />
                <input
                  type="text"
                  value={todayPlan?.notes || ''}
                  onChange={(e) => setTodayPlan((prev) => ({ ...(prev || {}), notes: e.target.value }))}
                  className="px-3 py-2.5 border border-stone-200 rounded-xl text-sm text-[#292524]"
                  placeholder={lang === 'hr' ? 'Kratke bilješke' : 'Quick notes'}
                  data-testid="planner-today-plan-notes-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Outlook-Style Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-6 bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-3">
          {/* Left: Nav + Today */}
          <div className="flex items-center gap-2">
            <button onClick={() => navigateDate(-1)} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" data-testid="planner-nav-prev">
              <ChevronLeft className="w-5 h-5 text-stone-600" />
            </button>
            <button
              onClick={() => setSelectedDate(getLocalDateKey())}
              className="px-3 py-1.5 text-sm font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
              data-testid="planner-today-btn"
            >
              {lang === 'hr' ? 'Danas' : 'Today'}
            </button>
            <button onClick={() => navigateDate(1)} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" data-testid="planner-nav-next">
              <ChevronRight className="w-5 h-5 text-stone-600" />
            </button>
            <div className="ml-2">
              <p className="text-base font-semibold text-[#292524] leading-tight">{formatDate(selectedDate)}</p>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="text-xs text-primary bg-transparent cursor-pointer"
                data-testid="planner-date-picker"
              />
            </div>
          </div>
          
          {/* Right: View Mode Toggle */}
          <div className="inline-flex bg-stone-100 p-1 rounded-lg self-center sm:self-auto">
            {['day', 'week', 'month', 'timeline'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                data-testid={`planner-view-${mode}`}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === mode ? 'bg-white text-primary shadow-sm' : 'text-[#57534E] hover:text-[#292524]'
                }`}
              >
                {mode === 'day' ? (lang === 'hr' ? 'Dan' : 'Day') :
                 mode === 'week' ? (lang === 'hr' ? 'Tjedan' : 'Week') :
                 mode === 'month' ? (lang === 'hr' ? 'Mjesec' : 'Month') :
                 (lang === 'hr' ? 'P vs S' : 'P vs A')}
              </button>
            ))}
          </div>
        </div>

        {/* Prayer Times Section (Day View Only) */}
        {viewMode === 'day' && prayerTimes && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-[#292524] flex items-center gap-2">
                🕌 {lang === 'hr' ? 'Dnevni namazi' : 'Daily Prayers'}
              </h3>
              <button
                onClick={() => setShowPrayerTimes(!showPrayerTimes)}
                className="text-sm text-primary"
              >
                {showPrayerTimes 
                  ? (lang === 'hr' ? 'Sakrij' : 'Hide') 
                  : (lang === 'hr' ? 'Prikaži' : 'Show')}
              </button>
            </div>
            
            {showPrayerTimes && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(PRAYER_NAMES).filter(([key]) => key !== 'Sunrise').map(([key, prayer]) => (
                  <button
                    key={key}
                    onClick={() => togglePrayer(key)}
                    className={`p-4 rounded-xl text-center transition-all ${
                      isPrayerCompleted(key)
                        ? 'bg-green-100 border-2 border-green-500'
                        : 'bg-white/80 border border-stone-200 hover:border-green-300'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">{prayer.icon}</span>
                    <p className="font-medium text-[#292524]">
                      {lang === 'hr' ? prayer.hr : prayer.en}
                    </p>
                    <p className="text-sm text-[#57534E]">
                      {prayerTimes[key.toLowerCase()] || prayerTimes[key] || '--:--'}
                    </p>
                    {isPrayerCompleted(key) && (
                      <Check className="w-4 h-4 text-green-500 mx-auto mt-1" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Outlook-Style Calendar Grid */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="planner-calendar-grid">
          {/* Calendar Header */}
          <div className="bg-gradient-to-r from-[#6B7B3F] to-[#8B9A5B] text-white px-4 py-3 flex items-center justify-between">
            <h3 className="font-semibold text-sm">
              {viewMode === 'day' 
                ? formatDate(selectedDate)
                : viewMode === 'week'
                  ? (() => {
                      const start = getWeekStart(selectedDate);
                      const end = new Date(start);
                      end.setDate(end.getDate() + 6);
                      return `${start.getDate()}. - ${end.getDate()}. ${end.toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-US', { month: 'long', year: 'numeric' })}`;
                    })()
                  : viewMode === 'timeline'
                    ? `${formatDate(selectedDate)} · ${lang === 'hr' ? 'Planirano vs stvarno' : 'Planned vs Actual'}`
                  : parseLocalDateKey(selectedDate).toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm opacity-80">
                {viewMode === 'timeline'
                  ? `${(timelineData?.groupedPlanned || []).length + (timelineData?.groupedActual || []).length}`
                  : eventsForCurrentView.length} {lang === 'hr' ? (viewMode === 'timeline' ? 'grupa' : 'stavki') : (viewMode === 'timeline' ? 'groups' : 'items')}
              </span>
            </div>
          </div>

          {viewMode === 'day' ? (
            /* ===== DAY VIEW - Time Grid ===== */
            <DayViewGrid
              selectedDate={selectedDate}
              todayEvents={todayEvents}
              getEventTypeInfo={getEventTypeInfo}
              openEditModal={openEditModal}
              formatTime={formatTime}
              setNewEvent={setNewEvent}
              setShowModal={setShowModal}
              lang={lang}
            />
          ) : viewMode === 'week' ? (
            /* ===== WEEK VIEW - Outlook Style ===== */
            <WeekViewGrid
              selectedDate={selectedDate}
              events={events}
              getEventTypeInfo={getEventTypeInfo}
              openEditModal={openEditModal}
              formatTime={formatTime}
              setSelectedDate={setSelectedDate}
              setViewMode={setViewMode}
              lang={lang}
            />
          ) : viewMode === 'timeline' ? (
            <PlannedVsActualView timelineData={timelineData} lang={lang} />
          ) : (
            /* ===== MONTH VIEW - Calendar Grid ===== */
            <MonthViewGrid
              selectedDate={selectedDate}
              events={events}
              getEventTypeInfo={getEventTypeInfo}
              getIslamicEventForDate={getIslamicEventForDate}
              setSelectedDate={setSelectedDate}
              setViewMode={setViewMode}
              formatTime={formatTime}
              lang={lang}
            />
          )}
        </div>

        {/* Quick Event List */}
        {viewMode !== 'timeline' && eventsForCurrentView.length > 0 && (
          <div className="mt-6 bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="planner-event-list">
            <div className="bg-stone-50 px-4 py-3 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-semibold text-[#292524] flex items-center gap-2">
                <Clock className="w-4 h-4 text-stone-500" />
                {lang === 'hr' ? 'Lista događaja' : 'Event List'}
              </h3>
              <span className="text-xs text-stone-500">
                {eventsForCurrentView.length} {lang === 'hr' ? 'stavki' : 'items'}
              </span>
            </div>
            <div className="divide-y divide-stone-100 max-h-[300px] overflow-y-auto">
              {[...eventsForCurrentView]
                .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                .map(event => {
                const typeInfo = getEventTypeInfo(event.event_type);
                return (
                  <div 
                    key={event.event_id || event.id}
                    data-testid={`event-list-item-${event.event_id || event.id}`}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors ${
                      event.completed ? 'opacity-50' : ''
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${typeInfo.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm text-[#292524] truncate ${event.completed ? 'line-through' : ''}`}>
                        {event.title}
                      </p>
                      <p className="text-xs text-stone-500">
                        {formatTimeUTC(event.start_time)} {viewMode !== 'day' && `• ${event.start_time.split('T')[0]}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleComplete(event.event_id)}
                        data-testid={`toggle-complete-${event.event_id || event.id}`}
                        className={`p-1.5 rounded transition-colors ${event.completed ? 'bg-green-100 text-green-600' : 'hover:bg-stone-100 text-stone-400'}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteEvent(event.event_id)}
                        data-testid={`delete-event-${event.event_id || event.id}`}
                        className="p-1.5 rounded hover:bg-red-50 hover:text-red-500 text-stone-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {viewMode !== 'timeline' && eventsForCurrentView.length === 0 && (
          <div className="mt-6 text-center py-8 bg-white rounded-xl border-2 border-dashed border-stone-200">
            <Calendar className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-[#57534E] mb-3">
              {lang === 'hr' ? 'Nema planiranih događaja' : 'No scheduled events'}
            </p>
            <button
              onClick={openNewEventModal}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm"
            >
              <Plus className="w-4 h-4 inline mr-1" />
              {lang === 'hr' ? 'Dodaj događaj' : 'Add event'}
            </button>
          </div>
        )}

        {/* Export Info */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <h3 className="font-semibold text-blue-800 mb-2">
            📅 {lang === 'hr' ? 'Sinkronizacija s kalendarom' : 'Calendar Sync'}
          </h3>
          <p className="text-sm text-blue-700">
            {lang === 'hr' 
              ? 'Izvezi .ics datoteku i uvezi ju u Outlook, Google Calendar, Apple Calendar ili bilo koji drugi kalendar koji podržava iCal format.'
              : 'Export .ics file and import it into Outlook, Google Calendar, Apple Calendar, or any other calendar that supports iCal format.'}
          </p>
        </div>

        {/* Auto-add Prayers Button */}
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="font-semibold text-green-800 mb-1">
                🕌 {lang === 'hr' ? 'Dodaj namaze u kalendar' : 'Add prayers to calendar'}
              </h3>
              <p className="text-sm text-green-700">
                {lang === 'hr' 
                  ? `Automatski dodaj sve namaze s vremenima iz Vaktije`
                  : `Automatically add all prayers with times from Vaktija`}
              </p>
              {prayerTimes && (
                <p className="text-xs text-green-600 mt-1">
                  Fajr: {prayerTimes.fajr}, Dhuhr: {prayerTimes.dhuhr}, Asr: {prayerTimes.asr}, Maghrib: {prayerTimes.maghrib}, Isha: {prayerTimes.isha}
                </p>
              )}
              {!prayerTimes && (
                <p className="text-xs text-orange-600 mt-1">
                  {lang === 'hr' ? 'Učitavanje vremena namaza...' : 'Loading prayer times...'}
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                onClick={() => addPrayersToPlanner('day')}
                disabled={!prayerTimes}
                data-testid="add-prayers-day"
                className="px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <CalendarPlus className="w-4 h-4" />
                {lang === 'hr' ? 'Danas' : 'Today'}
              </button>
              <button
                onClick={() => addPrayersToPlanner('week')}
                disabled={!prayerTimes}
                data-testid="add-prayers-week"
                className="px-4 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <CalendarPlus className="w-4 h-4" />
                {lang === 'hr' ? 'Tjedan' : 'Week'}
              </button>
              <button
                onClick={() => addPrayersToPlanner('month')}
                disabled={!prayerTimes}
                data-testid="add-prayers-month"
                className="px-4 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <CalendarPlus className="w-4 h-4" />
                {lang === 'hr' ? 'Mjesec' : 'Month'}
              </button>
              <button
                onClick={() => addPrayersToPlanner('year')}
                disabled={!prayerTimes}
                data-testid="add-prayers-year"
                className="px-4 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <CalendarPlus className="w-4 h-4" />
                {lang === 'hr' ? 'Godina' : 'Year'}
              </button>
            </div>
            
            <p className="text-xs text-green-600 text-center">
              {lang === 'hr' 
                ? '⚡ Bulk dodavanje automatski dohvaća vremena namaza za svaki dan' 
                : '⚡ Bulk add automatically fetches prayer times for each day'}
            </p>
            
            {/* Cleanup duplicates button */}
            <button
              onClick={cleanupDuplicatePrayers}
              data-testid="cleanup-duplicates-btn"
              className="w-full mt-3 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-all flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {lang === 'hr' ? 'Obriši duplikate namaza' : 'Remove duplicate prayers'}
            </button>
          </div>
        </div>

        {/* Islamic Calendar - Upcoming Events */}
        <div className="mt-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#292524]">
                {lang === 'hr' ? 'Islamski Kalendar' : 'Islamic Calendar'}
              </h3>
              <p className="text-xs text-amber-700">
                {lang === 'hr' ? 'Nadolazeći važni datumi' : 'Upcoming important dates'}
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            {getUpcomingIslamicEvents(selectedDate, 5).map((event, idx) => {
              const eventDate = new Date(event.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
              
              return (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-xl ${
                    event.type === 'eid' ? 'bg-green-100 border border-green-200' :
                    event.type === 'ramadan' ? 'bg-emerald-100 border border-emerald-200' :
                    event.type === 'holy' ? 'bg-purple-100 border border-purple-200' :
                    event.type === 'hajj' ? 'bg-blue-100 border border-blue-200' :
                    event.type === 'newyear' ? 'bg-amber-100 border border-amber-200' :
                    'bg-white border border-stone-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg ${
                      event.type === 'eid' ? 'bg-green-200' :
                      event.type === 'ramadan' ? 'bg-emerald-200' :
                      event.type === 'holy' ? 'bg-purple-200' :
                      event.type === 'hajj' ? 'bg-blue-200' :
                      event.type === 'newyear' ? 'bg-amber-200' :
                      'bg-stone-200'
                    }`}>
                      {event.type === 'eid' ? '🎉' :
                       event.type === 'ramadan' ? '🌙' :
                       event.type === 'holy' ? '✨' :
                       event.type === 'hajj' ? '🕋' :
                       event.type === 'newyear' ? '🗓️' :
                       '📅'}
                    </div>
                    <div>
                      <p className="font-medium text-[#292524] text-sm">
                        {lang === 'hr' ? event.name_hr : event.name_en}
                      </p>
                      <p className="text-xs text-stone-600">
                        {eventDate.toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                  <div className={`text-xs font-medium px-2 py-1 rounded-full ${
                    daysUntil <= 7 ? 'bg-red-100 text-red-700' :
                    daysUntil <= 30 ? 'bg-amber-100 text-amber-700' :
                    'bg-stone-100 text-stone-600'
                  }`}>
                    {daysUntil === 0 ? (lang === 'hr' ? 'Danas!' : 'Today!') :
                     daysUntil === 1 ? (lang === 'hr' ? 'Sutra' : 'Tomorrow') :
                     lang === 'hr' ? `za ${daysUntil} dana` : `in ${daysUntil} days`}
                  </div>
                </div>
              );
            })}
          </div>
          
          {getUpcomingIslamicEvents(selectedDate, 5).length === 0 && (
            <p className="text-center text-stone-500 py-4">
              {lang === 'hr' ? 'Nema nadolazećih događaja' : 'No upcoming events'}
            </p>
          )}
        </div>
      </div>

      {/* Event Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)} data-testid="planner-event-modal-overlay">
          <div 
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
            onClick={e => e.stopPropagation()}
            data-testid="planner-event-modal"
          >
            <h2 className="text-xl font-heading font-bold text-[#292524] mb-6">
              {editingEvent ? (lang === 'hr' ? 'Uredi događaj' : 'Edit Event') : (lang === 'hr' ? 'Novi događaj' : 'New Event')}
            </h2>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Naslov' : 'Title'}
                </label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                  placeholder={lang === 'hr' ? 'Npr. Sabah namaz' : 'E.g. Fajr prayer'}
                  data-testid="planner-event-title-input"
                />
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Vrsta' : 'Type'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {EVENT_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setNewEvent(prev => ({ ...prev, event_type: type.id }))}
                      className={`p-3 rounded-lg text-center transition-all ${
                        newEvent.event_type === type.id 
                          ? `${type.color} text-white` 
                          : 'bg-stone-100 text-[#57534E]'
                      }`}
                      data-testid={`planner-event-type-${type.id}`}
                    >
                      <span className="text-lg">{type.icon}</span>
                      <p className="text-xs mt-1">{lang === 'hr' ? type.label_hr : type.label_en}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date/Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#292524] mb-2">
                    {lang === 'hr' ? 'Početak' : 'Start'}
                  </label>
                  <input
                    type="datetime-local"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, start_time: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                    data-testid="planner-event-start-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#292524] mb-2">
                    {lang === 'hr' ? 'Kraj' : 'End'}
                  </label>
                  <input
                    type="datetime-local"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, end_time: e.target.value }))}
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                    data-testid="planner-event-end-input"
                  />
                </div>
              </div>

              {/* Recurrence */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Ponavljanje' : 'Repeat'}
                </label>
                <select
                  value={newEvent.recurrence_rule}
                  onChange={(e) => setNewEvent(prev => ({ 
                    ...prev, 
                    recurrence_rule: e.target.value,
                    is_recurring: !!e.target.value
                  }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                  data-testid="planner-event-recurrence-select"
                >
                  {RECURRENCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {lang === 'hr' ? opt.label_hr : opt.label_en}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reminder */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Podsjetnik (minute prije)' : 'Reminder (minutes before)'}
                </label>
                <select
                  value={newEvent.reminder_minutes || ''}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, reminder_minutes: e.target.value ? parseInt(e.target.value) : null }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                  data-testid="planner-event-reminder-select"
                >
                  <option value="">{lang === 'hr' ? 'Bez podsjetnika' : 'No reminder'}</option>
                  <option value="5">5 min</option>
                  <option value="10">10 min</option>
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="60">1 {lang === 'hr' ? 'sat' : 'hour'}</option>
                </select>
              </div>

              {/* Link to Bereket Activity */}
              {customActivities.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-[#292524] mb-2">
                    {lang === 'hr' ? 'Poveži s Bereket aktivnosti' : 'Link to Barakah Activity'}
                  </label>
                  <select
                    value={newEvent.bereket_activity_id || ''}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, bereket_activity_id: e.target.value || null }))}
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg text-[#292524]"
                    data-testid="planner-event-activity-select"
                  >
                    <option value="">{lang === 'hr' ? 'Nije povezano' : 'Not linked'}</option>
                    {customActivities.map(activity => (
                      <option key={activity.activity_id} value={activity.activity_id}>
                        {lang === 'hr' ? activity.name_hr : activity.name_en} (+{activity.base_points})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {lang === 'hr' ? 'Opis (opcionalno)' : 'Description (optional)'}
                </label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg resize-none h-20 text-[#292524]"
                  data-testid="planner-event-description-input"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingEvent(null);
                }}
                className="flex-1 py-3 bg-stone-100 text-[#57534E] rounded-lg font-medium"
                data-testid="planner-event-cancel-button"
              >
                {lang === 'hr' ? 'Odustani' : 'Cancel'}
              </button>
              <button
                onClick={saveEvent}
                className="flex-1 py-3 bg-primary text-white rounded-lg font-medium"
                data-testid="planner-event-save-button"
              >
                {lang === 'hr' ? 'Spremi' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
